import { useEffect, useState } from 'react'
import { accessReviewApiService, type AccessReviewReport } from '../services/accessReviewApiService'
import { ApiError } from '../services/BaseApiService'
import { ROLE_LABELS, SecurityRole, UserStatus } from '../constants'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'


function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : 'Never'
}

/**
 * SOC 2 CC6 periodic access review: who has access, which of it is stale, and a button that records
 * the review in the compliance log once the admin has acted on it.
 */
export default function AccessReviewPage() {
  const [report, setReport] = useState<AccessReviewReport | null>(null)
  const [error, setError] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [recordedAt, setRecordedAt] = useState<Date | null>(null)

  useEffect(() => {
    accessReviewApiService.report()
      .then(setReport)
      .catch(err => setError(err instanceof ApiError ? err.message : 'Failed to load access review'))
  }, [])

  async function record() {
    setSaving(true)
    setError('')
    try {
      await accessReviewApiService.record(notes.trim() || null)
      setRecordedAt(new Date())
      setNotes('')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to record review')
    } finally {
      setSaving(false)
    }
  }

  const card: React.CSSProperties = {
    background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md,
    boxShadow: shadow.sm, overflow: 'hidden', marginBottom: spacing.lg,
  }
  const th: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: fontSize.xs, fontWeight: 500,
    fontFamily: fontFamily.mono, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`, background: colors.background,
  }
  const td: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
  }
  const flag = (on: boolean, text: string) => on
    ? <span style={{ color: colors.error, fontWeight: 500 }}>{text}</span>
    : <span style={{ color: colors.textMuted }}>—</span>

  return (
    <div>
      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, margin: 0 }}>
        Access Review
      </h1>
      <p style={{ color: colors.textSecondary, marginTop: spacing.xs, marginBottom: spacing.xl, fontSize: fontSize.base }}>
        Review who has access to this team, revoke what is no longer needed, then record the review.
      </p>

      {error && <div style={{ color: colors.error, marginBottom: spacing.md }}>{error}</div>}
      {!report && !error && <div style={{ color: colors.textSecondary }}>Loading…</div>}

      {report && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.md, marginBottom: spacing.lg }}>
            {[
              ['Active users', report.summary.activeUsers],
              ['Admins', report.summary.admins],
              ['Admins without MFA', report.summary.adminsWithoutMfa],
              [`No sign-in in ${report.staleThresholdDays}d`, report.summary.staleUsers],
              ['API keys', report.summary.apiKeyCount],
              [`Unused keys (${report.staleThresholdDays}d)`, report.summary.staleApiKeys],
            ].map(([label, value]) => (
              <div key={label} style={{ ...card, marginBottom: 0, padding: spacing.md, minWidth: 140 }}>
                <div style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>{label}</div>
                <div style={{ fontSize: fontSize.xl, fontWeight: 600, color: colors.text }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ ...card, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Users">
              <thead><tr>
                <th style={th}>User</th><th style={th}>Role</th><th style={th}>Status</th>
                <th style={th}>Last sign-in</th><th style={th}>MFA</th><th style={th}>Stale</th>
              </tr></thead>
              <tbody>
                {report.users.map(u => (
                  <tr key={u.userId}>
                    <td style={td}>{u.emailAddress}</td>
                    <td style={td}>{ROLE_LABELS[u.role] ?? `Role ${u.role}`}</td>
                    <td style={td}>{u.status === UserStatus.ACTIVE ? 'Active' : u.status === UserStatus.INVITED ? 'Invited' : 'Inactive'}</td>
                    <td style={td}>{when(u.lastSignInAt)}</td>
                    <td style={td}>{u.mfaEnabled ? 'On' : flag(u.role >= SecurityRole.TEAM_ADMIN, 'Off')}</td>
                    <td style={td}>{flag(u.staleSignIn, 'Stale')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ ...card, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="API keys">
              <thead><tr>
                <th style={th}>API key</th><th style={th}>Capabilities</th><th style={th}>Active</th>
                <th style={th}>Last used</th><th style={th}>Stale</th>
              </tr></thead>
              <tbody>
                {report.apiKeys.length === 0 && (
                  <tr><td style={{ ...td, color: colors.textSecondary }} colSpan={5}>No API keys.</td></tr>
                )}
                {report.apiKeys.map(k => (
                  <tr key={k.apiKeyId}>
                    <td style={td}>{k.name}</td>
                    <td style={{ ...td, fontFamily: fontFamily.mono, fontSize: fontSize.xs }}>{k.capabilities || 'all'}</td>
                    <td style={td}>{k.isActive ? 'Yes' : 'Revoked'}</td>
                    <td style={td}>{when(k.lastUsedAt)}</td>
                    <td style={td}>{flag(k.staleUnused, 'Stale')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ ...card, padding: spacing.md }}>
            <label htmlFor="review-notes" style={{ display: 'block', fontSize: fontSize.sm, color: colors.text, marginBottom: spacing.xs }}>
              Review notes (what was revoked or confirmed)
            </label>
            <textarea
              id="review-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              maxLength={2000}
              style={{ width: '100%', boxSizing: 'border-box', padding: spacing.sm, border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, fontFamily: fontFamily.sans }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginTop: spacing.sm }}>
              <button
                onClick={record}
                disabled={saving}
                style={{ padding: '7px 14px', background: colors.primary, color: '#fff', border: 'none', borderRadius: radius.sm, fontSize: fontSize.sm, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
              >
                {saving ? 'Recording…' : 'Record review'}
              </button>
              {recordedAt && (
                <span role="status" style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>
                  Review recorded {recordedAt.toLocaleString()}.
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
